import { cn } from '@rocco/utils/cn';
import { useFeature } from '@modules/root';
import { SubscribePublicationsTrigger } from '@modules-rocco/subscription/component/SubscribePublicationsTrigger';
import { SubscribeField } from '~/ui-rocco/pages/home-page/components/SubscribeField';
import { BLEED_GUTTER_X } from '~/ui-rocco/pages/detail-shared';

interface EditorialIntroStripProps {
    /** The standing line about the publication. */
    copy: string;
    className?: string;
}

/**
 * The line of intro copy and the subscribe field beneath the masthead.
 *
 * Figma 60:1974: an 82-tall strip on a 50px gutter, Gestura 33/1.25 copy in a
 * 966 column against a 616-wide pill field.
 *
 * The frame draws an email field with a Subscribe pill inside it. The chooser
 * BC-5003 introduced collects the address itself, so a field here would take one
 * and discard it — the control is the pill alone, matching how the footer and
 * the B2B form were converted. With the flag off it falls back to the legacy
 * field, which subscribes on submit.
 */
export const EditorialIntroStrip = ({
    copy,
    className,
}: EditorialIntroStripProps) => {
    const canChoosePublications = useFeature(
        'digital_publication_subscribe_2026',
    );

    return (
        <section
            aria-label="About ArchiPro publications"
            className={cn(
                'flex items-center justify-between gap-[2rem] px-[1.5rem]',
                'h-[5.125rem] max-md:h-auto max-md:flex-col max-md:items-start max-md:gap-[1.5rem]',
                BLEED_GUTTER_X,
                className,
            )}
        >
            <p className="m-0 w-[60.375rem] max-w-full shrink-0 font-gestura text-[2.0625rem] leading-[1.25] font-normal tracking-[-0.0516rem] text-[#181818] max-md:w-full max-md:text-[1.375rem]">
                {copy}
            </p>

            {canChoosePublications ? (
                // Sized by the trigger itself rather than to the frame. Scaled
                // up to the drawn 60x22.7 it sat a head taller than every other
                // button on the page and left its arrow, which does not scale
                // with the label, looking thin beside the text.
                <SubscribePublicationsTrigger
                    label="Subscribe"
                    className="shrink-0"
                />
            ) : (
                <SubscribeField
                    variant="article"
                    className="w-[38.5rem] shrink-0 max-md:w-full"
                />
            )}
        </section>
    );
};
